import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { useForm, FormProvider } from 'react-hook-form';
import {
  Button, Container, Stepper, Step, StepLabel, Grid
} from '@mui/material';
import { FooterContext } from '@seccon/core/contexts/footer';
import { FooterContainerWithLeftAside } from '@seccon/shared/ui/components/FooterContainer';
import Spinner from '@seccon/shared/ui/components/Spinner';
import EmployeeForm from './components/EmployeeForm';
import useCurrentUserInfo from './hooks/useCurrentUserInfo';
import useModeOfTransportation from './hooks/useModeOfTransportation';
import useCreateReport from './hooks/useCreateReport';
import useTravelReason from './hooks/useTravelReason';
import useRelationship from './hooks/useRelationship';
import DestinationsForm from './components/DestinationsForm';
import ForeignTravelOverviewForm from './components/ForeignTravelOverviewForm';
import PassportInformationForm from './components/PassportInformationForm';
import EmergencyContactsForm from './components/EmergencyContactsForm';
import DocumentsForm from './components/DocumentsForm';
import { ACTION_CREATE } from './constants';

const steps = [
  'Employee',
  'Destinations',
  'Overview',
  'Passport',
  'Emergency',
  'Documents'
];

const CreateForeignTravel = () => {
  const employFormRef = useRef();
  const destinationFormRef = useRef();
  const overviewFormRef = useRef();
  const passportFormRef = useRef();
  const emergencyFormRef = useRef();
  const documentsformRef = useRef();
  const methods = useForm({
    mode: 'all',
    defaultValues: {
    }
  });
  const [activeStep, setActiveStep] = useState(0);
  const [isValidating, setIsValidating] = useState(false);

  const currentUserInfo = useCurrentUserInfo();
  const modeOfTransportation = useModeOfTransportation();
  const travelReason = useTravelReason();
  const relationship = useRelationship();

  const { createReport, isSubmitting } = useCreateReport();

  const handleCancel = () => {
    window.location = '/employee/requestsAndReports';
  };

  const handleNext = async () => {
    let isForm1Valid = true;

    switch (activeStep) {
      case 0:
        isForm1Valid = await employFormRef.current.validate();
        break;
      case 1:
        isForm1Valid = await destinationFormRef.current.validate();
        break;
      case 2:
        isForm1Valid = await overviewFormRef.current.validate();
        break;
      case 3:
        isForm1Valid = await passportFormRef.current.validate();
        break;
      case 4:
        isForm1Valid = await emergencyFormRef.current.validate();
        break;
      case 5:
        isForm1Valid = await documentsformRef.current.validate();
        break;
      default:
        break;
    }
    if (isForm1Valid) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <EmployeeForm ref={employFormRef} initialData={currentUserInfo} action={ACTION_CREATE} />;
      case 1:
        return <DestinationsForm ref={destinationFormRef} initialData={modeOfTransportation} />;
      case 2:
        return <ForeignTravelOverviewForm ref={overviewFormRef} travelReason={travelReason} modeOfTransportation={modeOfTransportation} />;
      case 3:
        return <PassportInformationForm ref={passportFormRef} />;
      case 4:
        return <EmergencyContactsForm ref={emergencyFormRef} initialData={relationship} />;
      case 5:
        return <DocumentsForm ref={documentsformRef} setIsValidating={setIsValidating} />;
      default:
        return 'Unknown step';
    }
  };

  const { isValid } = methods.formState;

  return (
    <FormProvider {...methods}>
      <Container>
        <Stepper sx={{ m: 4 }} activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Container sx={{ paddingBottom: '2rem' }}>
          {getStepContent(activeStep)}
        </Container>
        <Footer
          activeStep={activeStep}
          isSubmitting={isSubmitting}
          isValid={isValid}
          isValidating={isValidating}
          onBack={handleBack}
          onNext={handleNext}
          onCancel={handleCancel}
          onSubmit={methods.handleSubmit(createReport)}
        />
      </Container>
    </FormProvider>
  );
};

const Footer = ({
  activeStep,
  isSubmitting,
  isValid,
  isValidating,
  onBack,
  onNext,
  onCancel,
  onSubmit
}) => {
  const footerEl = React.useContext(FooterContext);

  if (footerEl === null) {
    return null;
  }
  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === 5;

  const content = (
    <FooterContainerWithLeftAside id='foreignTravelReportFooter'>
      <Grid container justifyContent='space-between' alignItems='center'>
        <Grid item>
          <Grid container spacing={2}>
            <Grid item>
              <Button variant='outlined' onClick={onBack} disabled={isFirstStep}>
                PREVIOUS
              </Button>
            </Grid>
            <Grid item>
              <Button variant='outlined' color='primary' onClick={onNext} disabled={isLastStep}>
                NEXT
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container spacing={2}>
            <Grid item>
              <Button variant='outlined' color='primary' onClick={onCancel}>
                CANCEL
              </Button>
            </Grid>
            <Grid item>
              <Button variant='contained' color='primary' onClick={onSubmit} disabled={activeStep !== 5 || isSubmitting || !isValid || isValidating} sx={{ minWidth: '85px', minHeight: '36px' }}>
                {isSubmitting ? <Spinner /> : 'CREATE'}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </FooterContainerWithLeftAside>
  );

  return ReactDOM.createPortal(content, footerEl);
};

Footer.propTypes = {
  activeStep: PropTypes.number.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  isValid: PropTypes.bool.isRequired,
  isValidating: PropTypes.bool.isRequired,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

export default CreateForeignTravel
